/**
 * 素材库图片网格组件
 * 用于展示批次内的图片，支持多选和分页
 */

import type { ReactElement } from 'react'
import { useMemo, useCallback } from 'react'
import { Loader2, ImageOff, Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useCrawlBatchGoods } from '@/hooks/useCrawlBatchGoods'
import type { CrawlBatch } from '@/types/crawlBatch'

/** 每页显示数量 */
const PAGE_SIZE = 100

/**
 * 分页组件属性
 */
interface PaginationProps {
  /** 当前页码 */
  currentPage: number
  /** 总页数 */
  totalPages: number
  /** 页码变化回调 */
  onPageChange: (page: number) => void
}

/**
 * 分页组件
 * @param props 组件属性
 * @returns 组件元素
 */
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps): ReactElement {
  /**
   * 生成页码数组
   * @returns 页码数组（数字或省略号）
   */
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      pages.push(totalPages)
    }

    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1">
      <Button
        variant="outline"
        size="icon"
        className="h-7 w-7 border-border/60 transition-colors"
        onClick={(): void => {
          onPageChange(currentPage - 1)
        }}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {getPageNumbers().map((page, index) =>
        typeof page === 'number' ? (
          <Button
            key={page}
            variant={page === currentPage ? 'default' : 'outline'}
            size="icon"
            className={cn(
              'h-7 w-7 text-xs transition-colors',
              page !== currentPage && 'border-border/60'
            )}
            onClick={(): void => {
              onPageChange(page)
            }}
          >
            {page}
          </Button>
        ) : (
          <span
            key={`ellipsis-${String(index)}`}
            className="px-1 text-xs text-muted-foreground"
          >
            ...
          </span>
        )
      )}

      <Button
        variant="outline"
        size="icon"
        className="h-7 w-7 border-border/60 transition-colors"
        onClick={(): void => {
          onPageChange(currentPage + 1)
        }}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

/**
 * MaterialLibraryImageGrid 组件属性
 */
interface MaterialLibraryImageGridProps {
  /** 当前选中的批次 */
  batch: CrawlBatch | null
  /** 选中的 URL 集合 */
  selectedUrls: Set<string>
  /** 切换单个 URL 选择状态 */
  onToggleUrl: (url: string, batchId: string) => void
  /** 全选当前页 */
  onSelectAll: (urls: string[], batchId: string) => void
  /** 全选整个素材库 */
  onSelectWholeBatch?: (() => void) | undefined
  /** 取消全选当前批次 */
  onDeselectAll: (batchId: string) => void
  /** 最大可选数量（可选） */
  maxCount?: number
  /** 当前已选总数 */
  currentSelectedCount: number
  /** 是否正在全选整个素材库 */
  isSelectingWholeBatch?: boolean
}

/**
 * 素材库图片网格组件
 * @param props 组件属性
 * @returns 组件元素
 */
export function MaterialLibraryImageGrid({
  batch,
  selectedUrls,
  onToggleUrl,
  onSelectAll,
  onSelectWholeBatch,
  onDeselectAll,
  maxCount,
  currentSelectedCount,
  isSelectingWholeBatch = false,
}: MaterialLibraryImageGridProps): ReactElement {
  // 获取批次内的商品列表（分页）
  const { items, isLoading, error, total, page, setPage } = useCrawlBatchGoods(
    batch?.id ?? '',
    PAGE_SIZE
  )

  // 计算总页数
  const totalPages = useMemo((): number => {
    return Math.ceil(total / PAGE_SIZE)
  }, [total])

  // 提取所有图片 URL
  const imageUrls = useMemo((): string[] => {
    return items.map((item) => item.imageUrl).filter((url) => url !== '')
  }, [items])

  // 计算当前批次已选中的数量
  const selectedInBatchCount = useMemo((): number => {
    if (batch === null) return 0
    return imageUrls.filter((url) => selectedUrls.has(url)).length
  }, [batch, imageUrls, selectedUrls])

  // 是否当前页全选
  const isAllCurrentPageSelected = useMemo((): boolean => {
    if (imageUrls.length === 0) return false
    return imageUrls.every((url) => selectedUrls.has(url))
  }, [imageUrls, selectedUrls])

  // 计算还能选择多少张
  const remainingSlots = useMemo((): number => {
    if (maxCount === undefined) return Infinity
    return maxCount - currentSelectedCount
  }, [maxCount, currentSelectedCount])

  /**
   * 处理全选当前页
   */
  const handleSelectCurrentPage = useCallback((): void => {
    if (batch === null) return

    // 只选择当前页未选中的图片
    const unselectedUrls = imageUrls.filter((url) => !selectedUrls.has(url))
    const urlsToSelect = unselectedUrls.slice(0, remainingSlots)
    if (urlsToSelect.length > 0) {
      onSelectAll(urlsToSelect, batch.id)
    }
  }, [batch, imageUrls, selectedUrls, remainingSlots, onSelectAll])

  /**
   * 处理取消全选（取消该批次所有选择）
   */
  const handleDeselectAll = useCallback((): void => {
    if (batch === null) return
    onDeselectAll(batch.id)
  }, [batch, onDeselectAll])

  /**
   * 处理单个图片点击
   */
  const handleImageClick = useCallback(
    (url: string): void => {
      if (batch === null) return

      const isCurrentlySelected = selectedUrls.has(url)
      // 如果要选中且已达上限，则不允许
      if (!isCurrentlySelected && remainingSlots <= 0) {
        return
      }

      onToggleUrl(url, batch.id)
    },
    [batch, selectedUrls, remainingSlots, onToggleUrl]
  )

  // 未选择批次
  if (batch === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
        <ImageOff className="mb-2 h-12 w-12 opacity-50" />
        <p className="text-sm">请从左侧选择一个素材库</p>
      </div>
    )
  }

  // 加载中
  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Loader2 className="mb-2 h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">加载中...</p>
      </div>
    )
  }

  // 加载错误
  if (error !== null) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-destructive">
        <ImageOff className="mb-2 h-12 w-12 opacity-50" />
        <p className="text-sm">{error}</p>
      </div>
    )
  }

  // 空状态
  if (imageUrls.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
        <ImageOff className="mb-2 h-12 w-12 opacity-50" />
        <p className="text-sm">该素材库暂无图片</p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* 顶部工具栏 */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{batch.remark || '未命名素材库'}</span>
          <span className="text-sm text-muted-foreground">共 {total} 张</span>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSelectWholeBatch}
          disabled={
            onSelectWholeBatch === undefined ||
            isSelectingWholeBatch ||
            total === 0 ||
            remainingSlots <= 0
          }
        >
          {isSelectingWholeBatch && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          全选整个素材库
        </Button>
      </div>

      {/* 图片网格 */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-5 gap-3 p-4">
          {imageUrls.map((url, index) => {
            const isSelected = selectedUrls.has(url)
            const isDisabled = !isSelected && remainingSlots <= 0

            return (
              <div
                key={`${url}-${String(index)}`}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                onClick={(): void => {
                  if (!isDisabled) {
                    handleImageClick(url)
                  }
                }}
                onKeyDown={(e): void => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                    handleImageClick(url)
                  }
                }}
                className={cn(
                  'group relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 transition-all',
                  isSelected
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-transparent hover:border-muted-foreground/30',
                  isDisabled && 'cursor-not-allowed opacity-50'
                )}
              >
                <img
                  src={url}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />

                {/* 选中遮罩 */}
                {isSelected && (
                  <div className="absolute inset-0 bg-primary/10" />
                )}

                {/* 复选框 */}
                <div
                  className={cn(
                    'absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded border-2 transition-all',
                    isSelected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-white/80 bg-black/20 group-hover:border-white'
                  )}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* 底部工具栏 */}
      <div className="flex items-center justify-between border-t px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            已选 <span className="font-medium text-primary">{selectedInBatchCount}</span> 张
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSelectCurrentPage}
            disabled={isAllCurrentPageSelected || remainingSlots <= 0}
          >
            全选当前页
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleDeselectAll}
            disabled={selectedInBatchCount === 0}
          >
            取消全选
          </Button>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              第 {page}/{totalPages} 页
            </span>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  )
}
